import { useState, useMemo } from "react";
import { ArrowUpRight, Info, Minus, Plus, Wallet, CalendarClock, RefreshCw, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "@/components/landing/Reveal";
import { renderTitle } from "@/components/landing/utils";

const MXN = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
const fmtInput = (n) => (n ? Number(n).toLocaleString("es-MX") : "");
const parseInput = (s) => Number(String(s).replace(/[^\d]/g, "")) || 0;

const DEFAULT_TERMS = [
  { icon: Wallet, title: "Participas desde {{min}}", body: "Eliges el monto con el que entras al proyecto." },
  { icon: CalendarClock, title: "{{years}} años recibiendo utilidades", body: "La participación tiene una vigencia de {{years}} años con distribución mensual de utilidades reales: pago los primeros 10 días de cada mes." },
  { icon: RefreshCw, title: "Recompra + siguiente proyecto", body: "Al término, Rubí recompra tu participación y puedes volver a participar en otro proyecto de expansión." },
];

export default function SimuladorGanancias({ block }) {
  const cfg = block || {};
  const minK = cfg.min_capital ?? 250000;
  const maxK = cfg.max_capital ?? 5000000;
  const stepK = cfg.step_capital ?? 50000;
  const years = cfg.horizon_years ?? cfg.default_horizon ?? 5;
  const conservador = cfg.rate_conservador ?? 0.12;
  const objetivo = cfg.rate_objetivo ?? 0.22;
  const presets = cfg.presets?.length ? cfg.presets : [250000, 500000, 1000000];
  const labelConservador = cfg.label_conservador || "Escenario conservador";
  const labelObjetivo = cfg.label_objetivo || "Escenario objetivo";
  const disclaimer = cfg.disclaimer || "Simulación referencial · No constituye promesa de rendimiento. Los escenarios se calculan sobre la base de márgenes históricos de Rubí Perfumería en sucursales comparables. La estructura Asociación en Participación (AEP) distribuye utilidades reales mensualmente (pago los primeros 10 días de cada mes), sujeta a desempeño operativo, condiciones de mercado y aprobación de estados financieros. Rendimientos pasados no garantizan resultados futuros.";
  const terms = (cfg.terms?.length ? cfg.terms : DEFAULT_TERMS).map((t, i) => ({ ...DEFAULT_TERMS[i], ...t }));
  const fill = (s) => String(s || "").replace(/\{\{\s*min\s*\}\}/g, MXN.format(minK)).replace(/\{\{\s*years\s*\}\}/g, years);

  const [capital, setCapital] = useState(cfg.default_capital ?? 500000);
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState("");
  const isPreset = presets.includes(capital);

  const clamp = (v) => Math.min(maxK, Math.max(minK, v));
  const commitDraft = () => { const v = parseInput(draft); if (v) setCapital(clamp(Math.round(v / stepK) * stepK)); setDraft(""); };

  const proj = useMemo(() => {
    const annualCons = capital * conservador, annualObj = capital * objetivo;
    return { annualCons, annualObj, qCons: annualCons / 4, qObj: annualObj / 4, totalCons: annualCons * years, totalObj: annualObj * years, finalCons: capital + annualCons * years, finalObj: capital + annualObj * years };
  }, [capital, conservador, objetivo, years]);

  const shareOf = Number(cfg.project_capital) || 5000000;
  const share = (capital / shareOf) * 100;

  if (cfg.visible === false) return null;

  const rows = [
    { label: "Utilidad mensual", cons: proj.annualCons / 12, obj: proj.annualObj / 12 },
    { label: "Utilidad por año", cons: proj.annualCons, obj: proj.annualObj },
    { label: `Utilidad total en ${years} años`, cons: proj.totalCons, obj: proj.totalObj, strong: true },
    { label: "Recompra de tu participación", cons: capital, obj: capital, note: "Recuperas tu capital al término" },
  ];

  return (
    <section id="simulador" className="bg-[#FAFAFA] py-20 md:py-32 relative overflow-hidden" data-testid="simulator-section">
      <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="max-w-3xl mb-10 md:mb-12">
          <Reveal><span className="eyebrow">{cfg.eyebrow || "Simulador de participación"}</span></Reveal>
          <Reveal delay={120}>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] leading-[1.02]">{renderTitle(cfg.title || "Explora tu <em>proyección</em> patrimonial.")}</h2>
          </Reveal>
          {cfg.subtitle && <Reveal delay={220}><p className="mt-5 text-[#3F3F46] text-base md:text-lg leading-relaxed">{cfg.subtitle}</p></Reveal>}
        </div>

        <Reveal delay={200} scale>
          <div className="rounded-3xl bg-white border border-black/8 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.25)] overflow-hidden">
            <div className="grid lg:grid-cols-[1fr_1.25fr]">
              {/* Left · capital */}
              <div className="p-6 md:p-10 border-b lg:border-b-0 lg:border-r border-black/8">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono bg-[#0A0A0A] text-[#FFBF57] px-3 py-1.5 rounded-full" data-testid="sim-min-badge">
                  Participación desde {MXN.format(minK)} MXN
                </div>

                <div className="mt-6 text-[11px] uppercase tracking-widest text-[#52525B] font-mono">1 · Elige tu capital</div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="sim-presets">
                  {presets.map((p) => (
                    <button key={p} type="button" onClick={() => { setCapital(clamp(p)); setCustom(false); }} data-testid={`sim-preset-${p}`}
                      className={`px-3 py-3 rounded-2xl text-sm num-mono transition-all border ${!custom && capital === p ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-[#FAFAFA] text-[#0A0A0A] border-black/8 hover:border-black/30"}`}>
                      {MXN.format(p)}
                    </button>
                  ))}
                  <button type="button" onClick={() => { setCustom(true); setDraft(fmtInput(capital)); }} data-testid="sim-custom-toggle"
                    className={`px-3 py-3 rounded-2xl text-xs font-mono uppercase tracking-widest transition-all border ${custom || !isPreset ? "bg-[#FFBF57] text-[#0A0A0A] border-[#FFBF57]" : "bg-[#FAFAFA] text-[#52525B] border-dashed border-black/25 hover:border-black/50"}`}>
                    Otro monto
                  </button>
                </div>

                {(custom || !isPreset) && (
                  <div className="mt-4 rounded-2xl border border-[#FFBF57] bg-[#FFBF57]/[0.06] p-4" data-testid="sim-custom-panel">
                    <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono mb-2">Escribe tu monto (mín. {MXN.format(minK)} · múltiplos de {MXN.format(stepK)})</div>
                    <div className="flex items-center gap-2">
                      <button type="button" aria-label="Menos" onClick={() => setCapital((c) => clamp(c - stepK))} className="w-11 h-11 rounded-xl border border-black/10 bg-white flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors" data-testid="sim-custom-minus"><Minus size={16} /></button>
                      <div className="flex-1 flex items-center rounded-xl border border-black/10 bg-white px-3 h-11">
                        <span className="num-mono text-[#52525B] mr-1">$</span>
                        <input inputMode="numeric" value={draft !== "" ? draft : fmtInput(capital)} onChange={(e) => setDraft(fmtInput(parseInput(e.target.value)))} onBlur={commitDraft} onKeyDown={(e) => e.key === "Enter" && commitDraft()}
                          className="w-full bg-transparent outline-none num-mono text-lg text-[#0A0A0A]" data-testid="sim-custom-input" />
                        <span className="text-[10px] font-mono text-[#52525B] ml-1">MXN</span>
                      </div>
                      <button type="button" aria-label="Más" onClick={() => setCapital((c) => clamp(c + stepK))} className="w-11 h-11 rounded-xl border border-black/10 bg-white flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors" data-testid="sim-custom-plus"><Plus size={16} /></button>
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-widest text-[#52525B] font-mono">Capital a participar</span>
                    <span className="text-[10px] text-[#A1A1AA] font-mono">hasta {MXN.format(maxK)}</span>
                  </div>
                  <div className="text-4xl md:text-5xl num-mono text-[#0A0A0A] tracking-tight" data-testid="sim-capital-value">{MXN.format(capital)}</div>
                  <div className="mt-3 flex items-center gap-3 rounded-2xl bg-[#0A0A0A] text-white px-4 py-3" data-testid="sim-share">
                    <div className="num-mono text-2xl md:text-3xl text-[#FFBF57]">{share.toLocaleString("es-MX", { maximumFractionDigits: 2 })}%</div>
                    <div className="text-xs leading-snug text-white/80">de participación en las utilidades del proyecto<span className="block text-[10px] text-white/50 font-mono mt-0.5">{MXN.format(capital)} de {MXN.format(shareOf)} · capital total del proyecto</span></div>
                  </div>
                  <input type="range" min={minK} max={maxK} step={stepK} value={capital} onChange={(e) => setCapital(Number(e.target.value))}
                    className="mt-4 w-full accent-[#FFBF57] cursor-pointer h-1.5 rounded-full appearance-none"
                    style={{ background: `linear-gradient(90deg, #FFBF57 0%, #FFBF57 ${((capital - minK) / (maxK - minK)) * 100}%, #E4E4E7 ${((capital - minK) / (maxK - minK)) * 100}%, #E4E4E7 100%)` }}
                    data-testid="sim-capital-slider" />
                </div>

                <div className="mt-10 text-[11px] uppercase tracking-widest text-[#52525B] font-mono">2 · Así funciona tu participación</div>
                <ol className="mt-4 space-y-4" data-testid="sim-terms">
                  {terms.map((t, i) => {
                    const Icon = t.icon || Wallet;
                    return (
                      <li key={i} className="flex gap-4">
                        <div className="w-9 h-9 shrink-0 rounded-xl bg-[#0A0A0A] flex items-center justify-center"><Icon size={15} className="text-[#FFBF57]" strokeWidth={1.8} /></div>
                        <div>
                          <div className="text-sm font-semibold text-[#0A0A0A]">{fill(t.title)}</div>
                          <div className="text-xs text-[#52525B] leading-relaxed mt-0.5">{fill(t.body)}</div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>

              {/* Right · results */}
              <div className="p-6 md:p-10 bg-[#0A0A0A] on-onyx text-white">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="text-[11px] uppercase tracking-widest text-[#FFBF57] font-mono">3 · Tu proyección a {years} años</div>
                  <div className="text-[10px] font-mono text-white/50 uppercase tracking-widest">Con {MXN.format(capital)}</div>
                </div>

                <div className="mt-6 grid grid-cols-[1.2fr_1fr_1fr] text-[10px] uppercase tracking-widest font-mono text-white/50 pb-3 border-b border-white/10">
                  <div />
                  <div className="text-right">{labelConservador}<div className="text-white/80 num-mono normal-case tracking-normal text-sm mt-0.5">{Math.round(conservador * 100)} % anual</div></div>
                  <div className="text-right text-[#FFBF57]">{labelObjetivo}<div className="text-[#FFBF57] num-mono normal-case tracking-normal text-sm mt-0.5">{Math.round(objetivo * 100)} % anual</div></div>
                </div>
                <div className="divide-y divide-white/10" data-testid="sim-results-table">
                  {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-[1.2fr_1fr_1fr] gap-2 py-4 items-baseline" data-testid={`sim-row-${i}`}>
                      <div>
                        <div className={`text-sm ${r.strong ? "text-white font-semibold" : "text-white/80"}`}>{r.label}</div>
                        {r.note && <div className="text-[10px] text-white/40 mt-0.5">{r.note}</div>}
                      </div>
                      <div className={`text-right num-mono ${r.strong ? "text-lg md:text-xl text-white" : "text-sm md:text-base text-white/70"}`}>{MXN.format(r.cons)}</div>
                      <div className={`text-right num-mono ${r.strong ? "text-lg md:text-xl text-[#FFBF57]" : "text-sm md:text-base text-[#FFBF57]/90"}`}>{MXN.format(r.obj)}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-2xl border border-[#FFBF57]/40 bg-[#FFBF57]/[0.08] p-5" data-testid="sim-final">
                  <div className="text-[10px] uppercase tracking-widest text-[#FFBF57] font-mono">Total al término · capital recuperado + utilidades</div>
                  <div className="mt-2 grid sm:grid-cols-2 gap-3 items-end">
                    <div>
                      <div className="text-[10px] font-mono text-white/50 uppercase tracking-widest">{labelConservador}</div>
                      <div className="text-2xl md:text-3xl num-mono text-white">{MXN.format(proj.finalCons)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono text-[#FFBF57] uppercase tracking-widest">{labelObjetivo}</div>
                      <div className="text-3xl md:text-4xl num-mono text-[#FFBF57]" data-testid="sim-final-obj">{MXN.format(proj.finalObj)}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-[11px] text-white/60"><ArrowRight size={12} className="text-[#FFBF57]" /> Después puedes participar en otro proyecto de expansión.</div>
                </div>

                <Link to="/calificacion" className="btn-primary w-full justify-center mt-6 !bg-[#FFBF57] !text-[#0A0A0A]" data-testid="sim-cta-formulario">
                  Aplicar con {MXN.format(capital)} <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>

            <div className="px-6 md:px-10 py-5 border-t border-black/8 flex items-start gap-3 text-[11px] text-[#52525B] leading-relaxed">
              <Info size={13} className="text-[#FFBF57] shrink-0 mt-0.5" />
              <div>{disclaimer}</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
